/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { Label, Select, Text, TextInput, Textarea } from '@/shared/ui';
import { CheckboxRaw, DateField } from '@/shared/ui/components/form/inputs';
import { cn } from '@/shared/ui/utils/tailwind';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { labelStyle, errorStyle } from './drawer-styles';

type SelectData = Array<{ value: string; label: string }>;

export type EventDrawerFieldsProps = {
  form: UseFormReturn<any>;
  calendarData: SelectData;
  recurrenceData: SelectData;
  allDay: boolean;
  calendarsCount: number;
  hasValidationErrors: boolean;
  readOnly?: boolean;
};

const fieldStack = cn('flex flex-col');
const twoCol = cn('grid grid-cols-2 gap-(--space-3)');

/**
 * Full form body for the event drawer (event, date/time, description).
 * @param props - Form instance, calendar/recurrence data, and layout flags.
 * @returns The event drawer form fields element.
 */
export function EventDrawerFields({
  form,
  calendarData,
  recurrenceData,
  allDay,
  calendarsCount,
  hasValidationErrors,
  readOnly = false,
}: EventDrawerFieldsProps) {
  const { t } = useTranslation('features/agenda');
  return (
    <fieldset disabled={readOnly} className="flex flex-col gap-(--space-3) border-0 p-0 m-0 min-w-0">
      <FormSection title={t('drawer.event')} variant="quiet">
        <Controller
          name="title"
          control={form.control}
          render={({ field, fieldState }) => (
            <div className={fieldStack}>
              <Label style={labelStyle}>
                {t('form.event_title')} <Text component="span" className="text-(--status-error)">*</Text>
              </Label>
              <TextInput
                {...field}
                placeholder={t('drawer.title_placeholder')}
                maxLength={FORM_FIELD_LIMITS.title}
                data-field="title"
                aria-label={t('form.event_title')}
              />
              {fieldState.error && <Text component="span" style={errorStyle}>{fieldState.error.message}</Text>}
            </div>
          )}
        />

        <Controller
          name="calendarId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Select
              label={t('form.calendar')}
              required
              data={calendarData}
              value={field.value || null}
              onChange={(v) => field.onChange(Array.isArray(v) ? v[0] : v)}
              placeholder={calendarsCount === 0 ? t('drawer.no_calendar_option') : undefined}
              error={fieldState.error?.message}
            />
          )}
        />
      </FormSection>

      <FormSection title={t('drawer.date_time')} variant="quiet">
        <div data-testid="event-fields-date-allday-row" className={twoCol}>
          <Controller
            name="date"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className={fieldStack}>
                <Label style={labelStyle}>
                  {t('form.date')} <Text component="span" className="text-(--status-error)">*</Text>
                </Label>
                <DateField
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  data-field="date"
                />
                {fieldState.error && <Text component="span" style={errorStyle}>{fieldState.error.message}</Text>}
              </div>
            )}
          />

          <Controller
            name="allDay"
            control={form.control}
            render={({ field }) => (
              // Sits in the second column of the date row, so it aligns with
              // the date input's box rather than with its label.
              <div className={cn(fieldStack, 'justify-end')}>
                <Label
                  onClick={() => field.onChange(!field.value)}
                  className={cn(
                    'flex h-11 items-center gap-(--space-2) cursor-pointer',
                    'rounded-(--radius-md) border border-[var(--border-subtle)]',
                    'bg-[var(--surface-primary)] px-(--space-3)',
                    'transition-colors hover:border-[var(--seed-base)]',
                    'font-[family-name:var(--font-family)]',
                    'font-[weight:var(--font-medium)]',
                    'text-[length:var(--text-small)]',
                    'leading-[var(--leading-snug)]',
                    'text-(--text-primary)',
                  )}
                >
                  <CheckboxRaw
                    value={!!field.value}
                    onToggle={(v) => field.onChange(v)}
                  />
                  {t('drawer.all_day')}
                </Label>
              </div>
            )}
          />
        </div>

        {!allDay && (
          <div data-testid="event-fields-time-row" className={twoCol}>
            <Controller
              name="startTime"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className={fieldStack}>
                  <Label style={labelStyle}>{t('form.start')}</Label>
                  <TextInput
                    type="time"
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
    
                  />
                  {fieldState.error && <Text component="span" style={errorStyle}>{fieldState.error.message}</Text>}
                </div>
              )}
            />
            <Controller
              name="endTime"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className={fieldStack}>
                  <Label style={labelStyle}>{t('form.end')}</Label>
                  <TextInput
                    type="time"
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
    
                  />
                  {fieldState.error && <Text component="span" style={errorStyle}>{fieldState.error.message}</Text>}
                </div>
              )}
            />
          </div>
        )}

        <Controller
          name="frequency"
          control={form.control}
          render={({ field }) => (
            <Select
              label={t('form.recurrence')}
              data={recurrenceData}
              value={field.value || null}
              onChange={(v) => field.onChange(Array.isArray(v) ? v[0] : v)}
            />
          )}
        />
      </FormSection>

      <div className={fieldStack}>
        <Label style={labelStyle}>{t('form.description')}</Label>
        <Textarea
          {...form.register('description')}
          placeholder={t('drawer.description_placeholder')}
          rows={2}
          maxLength={FORM_FIELD_LIMITS.description}
          style={{ height: 'auto', resize: 'vertical', minHeight: '3rem' }}
        />
      </div>

      {hasValidationErrors && (
        <div
          className={cn(
            'px-(--space-3) py-(--space-2) rounded-(--radius-md)',
            'bg-(--status-error-bg) border border-(--status-error-border)',
            'text-[length:var(--text-small)] font-[family-name:var(--font-family)] text-(--status-error)',
          )}
        >
          {t('drawer.fill_required')}
        </div>
      )}
    </fieldset>
  );
}
